import { Category, CategoryType } from '@model/Category';
import { Package, PackageType } from '@model/Package';
import { Sku, SkuType } from '@model/Sku';
import { call } from '@packages/functions';
import { AxiosPromise } from 'axios';
import { MutableRefObject } from 'react';

type ResourceType<P = any, S = any, C = any> = { packages: P | null; skus: S | null; categories: C | null };

export type ResourceErrorsType = ResourceType<Error, Error, Error>;
export type ResourceDataType = ResourceType<PackageType[], SkuType[], CategoryType[]>;

const MODELS = { packages: Package, skus: Sku, categories: Category };
const FIELDS = {
    packages: [
        'id',
        'name',
        'slug',
        'short_description',
        'description',
        'published',
        'customer_custom_prices',
        'price_range',
        'minimum_order_quantity',
        'international_minimum_order_quantity',
        ['category', ['id', 'name']],
        ['image', ['id', 'thumbnail_path']],
        [
            'subcategories',
            [
                'subcategory_to_repeat',
                ['subcategory', ['id', 'name']],
                'default_sku_id',
                [
                    'default_sku',
                    [
                        'id',
                        'name',
                        'minimum_order_quantity',
                        'international_minimum_order_quantity',
                        ['preview_photo', ['id', ' thumbnail_path']],
                    ],
                ],
            ],
        ],
    ],

    skus: [
        'id',
        'name',
        'slug',
        'category_id',
        'minimum_order_quantity',
        'international_minimum_order_quantity',
        'customer_custom_prices',
        'price_range',
        'internal_id',
        'is_sizing_kit',
        'short_description',
        ['preview_photo', ['id', 'thumbnail_path']],
        ['subcategory', ['id', 'name']],
        [
            'categories_subcategories',
            [
                'category_id',
                'default_sku',
                'default_sku_id',
                'id',
                'meta_description',
                'name',
                'published',
                'slug',
                'product_title',
                ['product_image', ['thumbnail_path']],
            ],
        ],
    ],

    categories: ['id', 'name'],
};

const _getResource = <K extends keyof typeof MODELS>(
    type: K,
    params: { [key: string]: any },
    transformer?: any,
): AxiosPromise<ResourceDataType[K]> => {
    const model = MODELS[type] as any;
    const params$ = {
        all: true,
        order_by: 'name',
        order_direction: 'asc',
        with_price: true,
        fields: FIELDS[type],
        ...params,
    };

    const { params: p, ...config } = (call(transformer, { params: params$ }) ?? { params: params$ }) as any;

    return model.index(p, config);
};

export const createResource = <K extends keyof ResourceDataType>(
    type: K,
    dataRef: MutableRefObject<ResourceDataType>,
    errorRef: MutableRefObject<ResourceErrorsType>,
    params: { [key: string]: any } = {},
    prepareConfig?: any,
    transformData?: any,
) => {
    return new Promise<ResourceDataType[K]>((resolve) => {
        _getResource(type, params, prepareConfig)
            .then(
                ({ data }: any) => (dataRef.current[type] = data?.items?.length ? call<any>(transformData, data) : []),
            )
            .catch((error) => (errorRef.current[type] = error))
            .finally(resolve as any);
    });
};
